import { useState } from 'react';
import { useSnowplowTracking, useTranslation } from '@eti/providers';
import { DangerousHtmlContent, Icon } from '@eti/components';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { vars, Inline, Text, textVariants, LinkButton } from '@etg/wings';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { ONE_WAY_COMBINATION } from '../../../constants/tripCharacteristics';
import CFCModal from '../../../common/cfc-modal/CFCModal';

const wrapperStyles = css`
  background-color: ${vars.colors.signal.information.tint};
  padding: 0.375rem 0.675rem;
`;

const buttonStyles = css`
  border: none;
  border-radius: 0;
  box-shadow: none;
  display: inline-block;
  text-align: start;
  width: 100%;

  &:focus {
    outline: none;
  }
`;

const iconStyles = css`
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

const caretStyles = css`
  margin-inline-start: auto;
  min-width: 20px;
`;

const htmlContentStyle = css`
  font-size: 0.875rem;
  margin-inline-start: 1.2rem;
  margin-top: 8px;
`;

const divIndentedStyle = css`
  padding-left: 1.2rem;
`;

const spanButtonStyle = css`
  font-size: 0.875rem;
  margin-top: 8px;
`;

const LinkButtonStyle = css`
  font-size: 0.875rem;
  margin-left: 5px;
  margin-top: 8px;
`;

type TripDetailsOneWayCombinationProps = {
  flightId: string;
  infoType?: string;
};

const TripDetailsInfoAccordion = ({ flightId, infoType }: TripDetailsOneWayCombinationProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();

  const handleOpenCFCModal = () => {
    setIsModalOpen(true);
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Results',
        action: 'Click',
        label: 'Booking process information',
        property: 'Ryanair',
      });
    }
  };

  return (
    <div className={wrapperStyles}>
      <button
        aria-controls={`accordionSection-${infoType ?? 'CARRIER_FLIGHT_CONFIRMATION'}-${flightId}`}
        aria-expanded={isOpen}
        className={cx(resetButtonStyle, buttonStyles)}
        data-testid={`tripDetails-${infoType ?? 'CARRIER_FLIGHT_CONFIRMATION'}-wrapper-${flightId}`}
        id={`accordionButton-${infoType ?? 'CARRIER_FLIGHT_CONFIRMATION'}-wrapper-${flightId}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <Inline align="center" alignY="top" noWrap spacing={4}>
          <Icon
            className={iconStyles}
            color={vars.colors.signal.information.light}
            id="info-circle"
            size="1rem"
          />
          <Text as="h3" variant={textVariants._14_BOLD}>
            {infoType === ONE_WAY_COMBINATION
              ? t('Air.TripInformation.OneWayCombination')
              : t('CarrierFlightConfirmation.InfoBox.Title')}
          </Text>
          {isOpen ? (
            <CaretUp className={caretStyles} size={20} />
          ) : (
            <CaretDown className={caretStyles} size={20} />
          )}
        </Inline>
      </button>
      {isOpen && (
        <>
          {infoType === ONE_WAY_COMBINATION ? (
            <DangerousHtmlContent
              aria-labelledby="accordionButton"
              className={htmlContentStyle}
              component="span"
              content={t('Air.TripInformation.OneWayCombination.Tooltip')}
              id={`accordionSection-${infoType}-${flightId}`}
              role="region"
            />
          ) : (
            <div
              className={divIndentedStyle}
              id={`accordionSection-CARRIER_FLIGHT_CONFIRMATION-${flightId}`}
            >
              <span className={spanButtonStyle}>
                {t('CarrierFlightConfirmation.InfoBox.Description')}
              </span>
              <LinkButton
                className={LinkButtonStyle}
                data-testid={`cfc-tripDetails-linkButton-${flightId}`}
                onClick={handleOpenCFCModal}
              >
                {t('CarrierFlightConfirmation.InfoBox.AnchorButton.Text')}
              </LinkButton>
            </div>
          )}
        </>
      )}
      <CFCModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} shouldShowProceedButton={false} />
    </div>
  );
};

export default TripDetailsInfoAccordion;
